<script setup>
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { nativeApps } from '../configuration.js'
import { useAnalytics } from '@composables/use-analytics'
import { isIosBrowser, isInAppBrowser } from '@utils/user-agent'

const EXCLUDED_PATHS = ['/account']
const APP_STORE_URL = `https://apps.apple.com/app/${nativeApps.iosAppId}`

function isExcludedPath(path) {
    return EXCLUDED_PATHS.some(p => path.startsWith(p))
}

const router = useRouter()
const analyticsService = useAnalytics()

const isVisible = ref(false)
const isOnExcludedPage = ref(isExcludedPath(window.location.pathname))

let unregisterHook = null

onMounted(() => {
    unregisterHook = router.beforeEach((to, from, next) => {
        isOnExcludedPage.value = isExcludedPath(to.path)
        next()
    })

    if (isIosBrowser() && !isInAppBrowser() && !isOnExcludedPage.value) {
        isVisible.value = true
        analyticsService.customIosAppBannerView()
    }
})

onBeforeUnmount(() => {
    if (unregisterHook) {
        unregisterHook()
    }
})

function dismissBanner() {
    isVisible.value = false
    analyticsService.customIosAppBannerDismiss()
}

function navigateToApp() {
    analyticsService.customIosAppBannerClick()
    window.location.href = APP_STORE_URL
}
</script>

<template>
    <div v-if="!isOnExcludedPage">
        <transition
            enter-active-class="slide-up-enter-active"
            leave-active-class="slide-up-leave-active"
            enter-from-class="slide-up-enter-from"
            leave-to-class="slide-up-leave-to"
        >
            <div
                v-if="isVisible"
                class="banner"
            >
                <button
                    class="close-button"
                    aria-label="Close"
                    @click="dismissBanner"
                >
                    <BaseIcon
                        name="xmark-circle"
                        class="close-icon"
                    />
                </button>
                <span class="text">FRISKY is better in the app</span>
                <BaseButton
                    button-type="white"
                    class="open-button"
                    :modifiers="{ autoWidth: true, roundCorners: true, noMargins: true }"
                    @click="navigateToApp"
                >Open</BaseButton>
            </div>
        </transition>
    </div>
</template>

<style lang="scss" scoped>
@use '@design' as *;

.banner {
    position: fixed;
    bottom: 0;
    left: 0;
    z-index: $layer-modal-z-index + 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: $s-l $s-m;
    padding-bottom: calc(#{$s-l} + env(safe-area-inset-bottom, 0));
    background: $c-shark-grey;
    border-radius: 20px 20px 0 0;
}

.close-button {
    flex-shrink: 0;
    padding: 0;
    cursor: pointer;
    outline: none;
    background: none;
    border: none;
    -webkit-tap-highlight-color: transparent;
}

.close-icon {
    fill: $c-white;

    @include square(30px);
}

.text {
    flex: 1;
    padding: 0 $s-s;
    font-size: 14px;
    font-weight: 500;
    color: $c-white;
    text-align: center;
}

.open-button {
    min-width: 136px;
    padding: 0 $s-xxl;
    font-size: $s-m;
    font-weight: 700;
    border-radius: 29px;
}

.slide-up-enter-active,
.slide-up-leave-active {
    transition: transform 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
    transform: translateY(100%);
}
</style>
